import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import SeoHead from './seo/SeoHead';
import NoIndexSeo from './seo/NoIndexSeo';
import { HOME_SERVICES_SCHEMA } from './seo/siteSeo';
import AOS from 'aos';
import Cursor from './components/Cursor';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Clients from './components/Clients';
import Services from './components/Services';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import ContactUs from './pages/ContactUs';
import Benefits from './components/Benefits';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import ChatBot from './components/ChatBot';
import './pages/Pages.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './config/fontawesome';

// Only lazy load non-critical components that don't affect layout
const ContactUsLazy = lazy(() => import('./pages/ContactUs'));
const ElyFleetLazy = lazy(() => import('./pages/ElyFleet'));
const ElyDineLazy = lazy(() => import('./pages/ElyDine'));
const ElyChatLazy = lazy(() => import('./pages/ElyChat'));
const ElyVentoryLazy = lazy(() => import('./pages/ElyVentory'));
const ElyCustomizedLazy = lazy(() => import('./pages/ElyCustomized'));

function ScrollToHash() {
    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.replace('#', ''));
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location]);
    return null;
}

function App() {
    const [darkMode, setDarkMode] = useState(false);
    const [audio, setAudio] = useState(null);
    const [siteLoaded, setSiteLoaded] = useState(false);
    const [barFull, setBarFull] = useState(false);
    const [showLoading, setShowLoading] = useState(true);
    const [isChatBotOpen, setIsChatBotOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const generateBrightColor = () => {
        // Generate a random hue between 0 and 360
        const hue = Math.floor(Math.random() * 360);
        // Use high saturation (80-100%) and high lightness (50-70%) for bright colors
        const saturation = Math.floor(Math.random() * 20) + 80;
        const lightness = Math.floor(Math.random() * 20) + 50;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    const setRandomColors = () => {
        const root = document.documentElement;
        // Generate three distinct colors
        let color1 = generateBrightColor();
        let color2 = generateBrightColor();
        let color3 = generateBrightColor();
        
        // Ensure colors are different enough
        while (color2 === color1) {
            color2 = generateBrightColor();
        }
        while (color3 === color1 || color3 === color2) {
            color3 = generateBrightColor();
        }

        root.style.setProperty('--color1', color1);
        root.style.setProperty('--color2', color2);
        root.style.setProperty('--color3', color3);
    };

    useEffect(() => {
        // Set initial random colors
        setRandomColors();

        // Add event listener for page load
        window.addEventListener('load', setRandomColors);

        AOS.init({
            duration: 1000,
            once: true,
            offset: 100,
        });

        // Cleanup
        return () => {
            window.removeEventListener('load', setRandomColors);
        };
    }, []);

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
            document.documentElement.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
            document.documentElement.classList.remove('dark-mode');
        }
    }, [darkMode]);

    useEffect(() => {
        const audioObj = new Audio('/sound.mp3');
        setAudio(audioObj);
    }, []);

    useEffect(() => {
        const handleLoad = () => setSiteLoaded(true);
        if (document.readyState === 'complete') {
            setSiteLoaded(true);
        } else {
            window.addEventListener('load', handleLoad);
        }

        // Safety fallback: Never keep the visitor waiting longer than 2.5s
        const fallbackTimer = setTimeout(() => {
            setSiteLoaded(true);
        }, 2500);

        return () => {
            window.removeEventListener('load', handleLoad);
            clearTimeout(fallbackTimer);
        };
    }, []);

    // Always show loading screen for at least 1.1s
    useEffect(() => {
        const timer = setTimeout(() => setBarFull(true), 1100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (siteLoaded && barFull) {
            setShowLoading(false);
        }
    }, [siteLoaded, barFull]);

    useEffect(() => {
        // Check if device is mobile
        const checkMobile = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        };
        setIsMobile(checkMobile());
    }, []);

    const loading = showLoading;

    const handleBarFull = useCallback(() => setBarFull(true), []);

    const toggleDarkMode = () => {
        setDarkMode((prev) => !prev);
        if (audio) {
            audio.currentTime = 0.25;
            audio.play();
        }
    };

    const toggleChatBot = () => {
        setIsChatBotOpen(!isChatBotOpen);
    };

    return (
        <>
            <LoadingScreen isVisible={loading} onBarFull={handleBarFull} />
            <Router>
                <ScrollToTop />
                <ScrollToHash />
                <div className={`App${darkMode ? ' dark-mode' : ''}`}>
                    <Cursor />
                    <ScrollProgress />
                    <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                    <Routes>
                        <Route path="/attendance" element={<Navigate to="/" replace />} />
                        <Route path="/elyticket" element={<Navigate to="/" replace />} />
                        <Route path="/elycrew" element={<Navigate to="/" replace />} />
                        <Route path="/elystitch" element={<Navigate to="/" replace />} />
                        <Route path="/elysched" element={<Navigate to="/" replace />} />
                        <Route path="/elydine" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ElyDineLazy />
                            </Suspense>
                        } />
                        <Route path="/elychat" element={<Navigate to="/elyconcierge" replace />} />
                        <Route path="/elyventory" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ElyVentoryLazy />
                            </Suspense>
                        } />
                        <Route path="/elycustomized" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ElyCustomizedLazy />
                            </Suspense>
                        } />
                        <Route path="/elyconcierge" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ElyChatLazy />
                            </Suspense>
                        } />
                        <Route path="/elyfleet" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ElyFleetLazy />
                            </Suspense>
                        } />
                        <Route path="/contact" element={
                            <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FontAwesomeIcon icon="spinner" spin size="2x" />
                            </div>}>
                                <ContactUsLazy />
                            </Suspense>
                        } />
                        <Route path="/" element={
                            <>
                                <SeoHead pageKey="home" schemas={[HOME_SERVICES_SCHEMA]} />
                                <Hero darkMode={darkMode} />
                                <Clients />
                                <Services />
                                <Benefits />
                                <Contact />
                            </>
                        } />
                    </Routes>
                    <Footer darkMode={darkMode} />
                    <ChatBot isOpen={isChatBotOpen} setIsOpen={setIsChatBotOpen} darkMode={darkMode} />
                </div>
                {/* Conditionally render FAB container */}
                {!isChatBotOpen && (
                    <div className={`fab-container ${darkMode ? 'dark-mode' : ''}`}>
                        {/* Email Button */}
                        <a
                            href={isMobile 
                                ? "mailto:info@elyspr.com?subject=Inquiry regarding Elyspr's services&body=Dear Elyspr Team,%0D%0A%0D%0AI hope this email finds you well. I am writing to express my interest in learning more about Elyspr's comprehensive range of services.%0D%0A%0D%0AI would appreciate if you could provide detailed information about your offerings and how they might benefit my organization.%0D%0A%0D%0AThank you for your time and consideration.%0D%0A%0D%0ABest regards,"
                                : "https://mail.google.com/mail/?view=cm&fs=1&to=info@elyspr.com&su=Inquiry regarding Elyspr's services&body=Dear Elyspr Team,%0D%0A%0D%0AI hope this email finds you well. I am writing to express my interest in learning more about Elyspr's comprehensive range of services.%0D%0A%0D%0AI would appreciate if you could provide detailed information about your offerings and how they might benefit my organization.%0D%0A%0D%0AThank you for your time and consideration.%0D%0A%0D%0ABest regards,"
                            }
                            className={`fab-button ${darkMode ? 'dark-mode' : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Send us an email"
                            data-tooltip="Reach out to us "
                        >
                            <FontAwesomeIcon icon="envelope" />
                        </a>
                        {/* Chatbot Button */}
                        <a
                            href="#"
                            className={`fab-button ${darkMode ? 'dark-mode' : ''}`}
                            aria-label="Open Chatbot"
                            data-tooltip="Chat with Elyspr AI"
                            onClick={toggleChatBot}
                        >
                            <FontAwesomeIcon icon="headset" />
                        </a>
                    </div>
                )}
            </Router>
        </>
    );
}

export default App;